import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { Key } from 'ts-key-enum';

@Component({
  selector: 'crm-galleria',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './galleria.component.html',
  styleUrls: ['./galleria.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class GalleriaComponent implements OnInit, OnChanges {
  @ViewChild('fullEl', { static: true }) public fullEl: ElementRef;
  @ViewChild('imageElement') public imageElement: ElementRef;
  @ViewChild('toolboxElement') public toolboxElement: ElementRef;

  private backElement: ElementRef;
  @ViewChild('backElement') public set contentBackElement(content: ElementRef) {
    if (content) {
      this.backElement = content;
    }
  }

  private nextElement: ElementRef;
  @ViewChild('nextElement') public set contentNextElement(content: ElementRef) {
    if (content) {
      this.nextElement = content;
    }
  }

  public readonly MAX_SCALE = 1.5;
  public readonly MIN_SCALE = 0.6;
  public readonly TIME_LOAD_IMAGE = 300;

  public isAnimatedFullShow = false;
  public isFullShow = false;
  public scale = 1.0;
  public rotate = 0;

  @Input() public visible: boolean;
  @Output() public visibleChange = new EventEmitter<boolean>();

  @Input() public activeImageIndex = 0;
  @Input() public images: { id: string; src: string; alt: string }[] = [];
  @Input() public appendTo: string;

  public get isShowNext(): boolean {
    return this.activeImageIndex + 1 !== this.images?.length;
  }

  public get isShowBack(): boolean {
    return !!this.activeImageIndex;
  }

  public ngOnInit(): void {
    if (this.appendTo === 'body' && this.fullEl) {
      document.body.appendChild(this.fullEl.nativeElement);
    }
    if (this.visible) {
      this.onShowImage();
    }
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.appendTo?.currentValue && this.appendTo === 'body' && this.fullEl) {
      document.body.appendChild(this.fullEl.nativeElement);
    }
    if (changes.visible?.currentValue) {
      this.onShowImage();
    }
    if (changes.visible && !changes.visible.currentValue) {
      this.onHideImage();
    }
  }

  @HostListener('document:keydown', ['$event'])
  public keyDown(event: KeyboardEvent) {
    if (event.key === Key.Escape && this.isFullShow && !this.isAnimatedFullShow) {
      this.onHideImage();
    }
    if (event.key === Key.ArrowRight && this.isFullShow && !this.isAnimatedFullShow && this.isShowNext) {
      this.nextImage();
    }
    if (event.key === Key.ArrowLeft && this.isFullShow && !this.isAnimatedFullShow && this.isShowBack) {
      this.backImage();
    }
  }

  @HostListener('document:click', ['$event'])
  public onClick(event: PointerEvent) {
    if (
      this.isFullShow &&
      !this.isAnimatedFullShow &&
      !(this.imageElement?.nativeElement as HTMLElement).contains(event.target as any) &&
      !(this.toolboxElement?.nativeElement as HTMLElement).contains(event.target as any) &&
      !(this.backElement?.nativeElement as HTMLElement).contains(event.target as any) &&
      !(this.nextElement?.nativeElement as HTMLElement).contains(event.target as any)
    ) {
      this.onHideImage();
    }
  }

  public onShowImage() {
    this.isAnimatedFullShow = true;
    this.isFullShow = true;
    setTimeout(() => {
      this.isAnimatedFullShow = false;
    }, this.TIME_LOAD_IMAGE);
  }

  public onHideImage() {
    this.isAnimatedFullShow = true;
    this.isFullShow = false;
    setTimeout(() => {
      this.scale = 1.0;
      this.rotate = 0;
      this.isAnimatedFullShow = false;
      this.visible = false;
      this.visibleChange.emit(false);
    }, this.TIME_LOAD_IMAGE);
  }

  public nextImage() {
    if (this.activeImageIndex + 1 !== this.images?.length) {
      this.activeImageIndex++;
    }
  }

  public backImage() {
    if (this.activeImageIndex) {
      this.activeImageIndex--;
    }
  }

  public plusScale() {
    this.scale += 0.1;
  }

  public minusScale() {
    this.scale -= 0.1;
  }

  public rotateLeft() {
    this.rotate += 90;
  }

  public rotateRight() {
    this.rotate -= 90;
  }
}
